import {FC, MouseEvent, useState} from 'react';
import {useCan} from 'src/permissions/hooks';
import {ContentCopyOutlined, LocationOnOutlined} from '@mui/icons-material';
import {Box, Button, IconButton, Popover, Typography} from '@mui/material';
import {Link} from 'react-router-dom';
import {useIntl} from 'react-intl';
import copy from 'copy-to-clipboard';
import {useScreenTier} from 'src/hooks/useScreenTier';
import {ActionSheet, ActionSheetDestructive} from 'src/components/mobile/ActionSheet';
import {PhoneCardHead} from 'src/components/mobile/PhoneCardHead';
import {PhoneCardFacts} from 'src/components/mobile/PhoneCardFacts';
import {phonePillsRowSx} from 'src/components/mobile/mobileDesign.sx';
import {UserIcon} from '../../../icons/UserIcon';
import {LightningIcon} from '../../../icons/LightningIcon';
import {NotifyError, NotifySuccess} from '../../../../utils/notification';
import {locationsApi} from '../../../../api';
import {useAppDispatch} from '../../../../app/hooks';
import {preloaderChangeStatus} from '../../../../stores/common/commonSlice';
import {AppDispatch} from '../../../../types';
import {AppRouteEnum, AppRouteSpace, formatPrice} from '../../../../utils/helper';
import {
    countPillSx,
    factWithActionSx,
    locationItemCardSx,
    nameLinkSx,
} from './LocationItemCard.sx';
import {MenuButton} from '../../../tables/respnsiveTable/MenuButton';
import {TableCard} from '../../../tables/respnsiveTable/TableCard';
import {LocationItemCardProps} from './LocationItemCard.types';

type DeleteLocationMutationTrigger = ReturnType<typeof locationsApi.useDeleteLocationMutation>[0];

const handleDeleteLocation =
    (
        trigger: DeleteLocationMutationTrigger,
        dispatch: AppDispatch,
        locationId: number,
        businessId: number
    ) =>
        async () => {
            dispatch(preloaderChangeStatus(true));
            try {
                await trigger({locationId, businessId}).unwrap();
            } catch (error: any) {
                NotifyError(error.data.message);
            } finally {
                dispatch(preloaderChangeStatus(false));
            }
        };

export const LocationItemCard: FC<LocationItemCardProps> = ({
                                                                id,
                                                                name,
                                                                address,
                                                                clientsCount,
                                                                chargePointsCount,
                                                                business,
                                                                price,
                                                                canCreateDeleteLocation = true,
                                                            }) => {
    // The menu exists only to offer this action, so the permission decides whether it appears.
    // Where a flag is also passed in, both must hold: the parent says whether the action belongs
    // on this screen, the permission says whether this person may perform it - and the prop
    // defaults to true, so on its own it fails open.
    const canDeleteLocation = useCan('locations.delete');
    const intl = useIntl();
    const [trigger] = locationsApi.useDeleteLocationMutation();
    const dispatch = useAppDispatch();
    // Below a desktop the menu is a sheet; from md up it stays the popover it has always been.
    const {isCompact} = useScreenTier();
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const copyToClipboard = () => {
        copy(String(id), {debug: true});
        NotifySuccess(intl.formatMessage({id: 'copiedSuccesfully'}));
    };

    // One node, drawn by whichever of the two the width calls for: the handler and the gate around
    // it are the same in both.
    const deleteAction = (
        <Button
            sx={locationItemCardSx.menuBtn}
            onClick={handleDeleteLocation(trigger, dispatch, +id, +business.id)}
        >
            <Typography sx={locationItemCardSx.menuBtnText}>
                {intl.formatMessage({id: 'delete'})}
            </Typography>
        </Button>
    );

    return (
        <TableCard>
            <PhoneCardHead
                icon={<LocationOnOutlined/>}
                title={(
                    <Box
                        component={Link}
                        to={AppRouteSpace([AppRouteEnum.app, AppRouteEnum.locations, String(id)])}
                        sx={nameLinkSx}
                    >
                        {name}
                    </Box>
                )}
                subtitle={`${address.street} ${address.houseNumber}`}
                action={canCreateDeleteLocation && canDeleteLocation && (
                    <MenuButton handleClick={handleClick}/>
                )}
            />
            {/* What the columns counted, as pills: the icon says what is being counted. */}
            <Box sx={phonePillsRowSx}>
                <Typography
                    component="span"
                    sx={countPillSx}
                >
                    <LightningIcon/>
                    {chargePointsCount}
                </Typography>
                <Typography
                    component="span"
                    sx={countPillSx}
                >
                    <UserIcon/>
                    {clientsCount}
                </Typography>
            </Box>
            <PhoneCardFacts
                facts={[
                    {
                        label: intl.formatMessage({id: 'locationId'}),
                        value: (
                            <Box
                                component="span"
                                sx={factWithActionSx}
                            >
                                {id}
                                <IconButton
                                    onClick={copyToClipboard}
                                    sx={locationItemCardSx.iconBtn}
                                >
                                    <ContentCopyOutlined sx={{width: '0.75rem'}}/>
                                </IconButton>
                            </Box>
                        ),
                    },
                    {
                        label: intl.formatMessage({id: 'country'}),
                        value: address?.country,
                    },
                    {
                        label: intl.formatMessage({id: 'city'}),
                        value: address?.city,
                    },
                    {
                        label: intl.formatMessage({id: 'price'}),
                        value: price ? `${formatPrice(price.defaultPrice)} ${price.currency}` : '-',
                    },
                ]}
            />
            {isCompact ? (
                <ActionSheet
                    open={open}
                    onClose={handleClose}
                    icon={<LocationOnOutlined/>}
                    title={name}
                    subtitle={`${address?.street ?? ''} ${address?.houseNumber ?? ''}`.trim()}
                >
                    <ActionSheetDestructive>{deleteAction}</ActionSheetDestructive>
                </ActionSheet>
            ) : (
                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    sx={locationItemCardSx.popover}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                >
                    {deleteAction}
                </Popover>
            )}
        </TableCard>
    );
};
